import classNames from 'classnames'
import IconButton from 'components/common/iconButton/IconButton'
import IconRender from 'components/common/IconRender'
import Loading from 'components/common/loading/Loading'
import Slide from 'components/common/Slide'
import ListingVerificationBadge from 'components/listings/ListingVerificationBadge'
import { formatListingAvailabilityLabel } from 'components/propertyCard/formatListingAvailability'
import ListingAddressRow from 'components/propertyCard/ListingAddressRow'
import {
  isListingUnavailable,
  listingCardHighlights,
} from 'components/propertyCard/listingCardHighlights'
import {
  listingRoomLabel,
  listingTypeLabel,
} from 'components/propertyCard/listingCardMeta'
import PropertySharePopup from 'components/propertyCard/PropertySharePopup'
import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { CustomColorsRef, IconSizes } from 'types/general'
import { ListedProperty, VerificationStatus } from 'types/property'

import Card from '../common/Card'
import CustomSlideButtons from './CustomSlideButton'
import { formatListingPrice } from './formatListingPrice'

interface PropertyCardProps {
  className?: string
  property: ListedProperty
  currentIndex: number | null
  listIndex: number
  handleClick?: () => void
  onMouseEnter?: () => void
  onMouseLeave?: () => void
  handleFavorite?: (propertyId: string) => void
  loadingFavoriteId?: string | null
}

const actionBtnClass =
  'relative flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-md transition-transform hover:scale-105 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-imovPrimary'

const PropertyCard = ({
  className,
  property,
  currentIndex,
  listIndex,
  loadingFavoriteId,
  handleClick,
  onMouseEnter,
  onMouseLeave,
  handleFavorite,
}: PropertyCardProps) => {
  const { t, i18n } = useTranslation()
  const [customPrev, setCustomPrev] = useState(0)
  const [customNext, setCustomNext] = useState(0)
  const [isHovered, setIsHovered] = useState(false)
  const [currentImage, setCurrentImage] = useState<number>(0)
  const [isShareOpen, setIsShareOpen] = useState(false)
  const shareAnchorRef = useRef<HTMLDivElement>(null)

  const unavailable = isListingUnavailable(property as Parameters<typeof isListingUnavailable>[0])
  const formattedPrice = formatListingPrice(property.price, property.currency)
  const roomLabel = listingRoomLabel(t, property.bedrooms)
  const leaseLabel = listingTypeLabel(t, property)
  const availabilityLabel = formatListingAvailabilityLabel(
    t,
    property.availability,
    i18n.language || 'fr-CA'
  )
  const highlights = useMemo(
    () => listingCardHighlights(t, property as unknown as Record<string, unknown>),
    [t, property]
  )
  const favoriteBusy = loadingFavoriteId === property.id

  const openListing = () => {
    if (unavailable) return
    handleClick?.()
  }

  return (
    <>
      <Card
        role="link"
        tabIndex={unavailable ? -1 : 0}
        aria-label={`${formattedPrice} · ${roomLabel}`}
        onClick={openListing}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            openListing()
          }
        }}
        onMouseEnter={() => {
          onMouseEnter?.()
          setIsHovered(true)
        }}
        onMouseLeave={() => {
          onMouseLeave?.()
          setIsHovered(false)
        }}
        className={classNames(
          'h-full min-h-0 transition-all duration-200',
          unavailable
            ? 'cursor-not-allowed opacity-75'
            : 'cursor-pointer hover:-translate-y-0.5 hover:border-imovPrimary/30 hover:shadow-lg focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-imovPrimary',
          className?.includes('w-full') ? '' : 'w-72 md:w-80',
          className
        )}
      >
        <div className="relative shrink-0 overflow-hidden">
          <Slide
            images={property.images}
            hiddeList
            hiddePreviews
            hiddeButtons
            customSize
            prev={customPrev}
            next={customNext}
            setCurrentImage={setCurrentImage}
            active={currentIndex === null || currentIndex === listIndex}
            customImageClass="h-52 w-full object-cover md:h-56"
          />

          {unavailable ? (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/55 px-4 text-center">
              <p className="rounded-full bg-white/95 px-3 py-1.5 text-sm font-semibold text-imovSecondary">
                {t('propertyCard.unavailable')}
              </p>
            </div>
          ) : null}

          {property.images?.length > 1 && !unavailable ? (
            <CustomSlideButtons
              className={classNames(
                'transition-opacity duration-200',
                isHovered ? 'opacity-100' : 'opacity-0 pointer-events-none'
              )}
              handlePrev={e => {
                e.stopPropagation()
                setCustomPrev(customPrev - 1)
              }}
              handleNext={e => {
                e.stopPropagation()
                setCustomNext(customNext + 1)
              }}
            />
          ) : null}

          {property.images?.length > 1 ? (
            <div className="absolute bottom-3 right-3 rounded-full bg-black/50 px-2 py-0.5 text-xs text-white">
              {currentImage + 1} / {property.images?.length}
            </div>
          ) : null}

          <div className="absolute left-3 top-3 z-20 flex max-w-[calc(100%-3.5rem)] items-center gap-1.5">
            {property.verified === VerificationStatus.Approved ? (
              <ListingVerificationBadge verified={property.verified} />
            ) : null}
            <span className="inline-flex min-w-0 truncate rounded-md bg-white/95 px-2 py-0.5 text-[11px] font-medium tracking-wide text-gray-600 shadow-sm ring-1 ring-black/5">
              {leaseLabel}
            </span>
          </div>

          <div ref={shareAnchorRef} className="absolute bottom-3 left-3 z-10 flex h-9 w-9">
            <IconButton
              icon="regular-share-nodes"
              size={IconSizes.LG}
              color="#111827"
              handleClick={e => {
                e.stopPropagation()
                e.preventDefault()
                setIsShareOpen(true)
              }}
              containerClassName={actionBtnClass}
            />
          </div>

          {handleFavorite ? (
            <div className="absolute right-3 top-3 z-10">
              {favoriteBusy ? (
                <Loading className="!p-0 h-9 w-9 rounded-full bg-white/90 shadow-sm" />
              ) : (
                <IconButton
                  icon={property.isFavorited ? 'solid-heart' : 'regular-heart'}
                  size={IconSizes.LG}
                  color={
                    property.isFavorited ? CustomColorsRef.Primary : '#111827'
                  }
                  handleClick={e => {
                    e.stopPropagation()
                    e.preventDefault()
                    if (favoriteBusy) return
                    handleFavorite?.(property.id)
                  }}
                  containerClassName={actionBtnClass}
                />
              )}
            </div>
          ) : null}
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-2.5 px-4 pb-4 pt-3">
          <div className="flex items-start justify-between gap-4">
            <p className="min-w-0 text-2xl font-bold leading-tight text-imovSecondary">
              {formattedPrice}
            </p>
            <p className="shrink-0 pt-1 text-lg font-semibold leading-none text-gray-800">
              {roomLabel}
            </p>
          </div>

          <p className="flex items-center gap-1.5 text-sm font-medium text-imovPrimary">
            <IconRender
              icon="regular-calendar"
              size={IconSizes.SM}
              color={CustomColorsRef.Primary}
            />
            <span>{availabilityLabel}</span>
          </p>

          <ListingAddressRow property={property} />

          {highlights.length > 0 ? (
            <p className="text-xs leading-relaxed text-gray-600">
              {highlights.join(' · ')}
            </p>
          ) : null}
        </div>
      </Card>

      <PropertySharePopup
        isOpen={isShareOpen}
        onClose={() => setIsShareOpen(false)}
        shareUrl={`${window.location.origin}/listing/${property.id}`}
        anchorRef={shareAnchorRef}
      />
    </>
  )
}

export default PropertyCard
